import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FilterOptionService, FILTER_OPTION_SERVICE_TOKEN } from '@shared/components/filters-bar/filter-option-handler';
import {
  FILTER_OPTIONS_CONFIG_TOKEN,
  FilterOptionConfig,
  FilterOptionMenu,
  INCLUDE_EMPTY,
  EXCLUDE_EMPTY,
  EXCLUDE_SELECTION,
  DEFAULT_OPT,
} from './filter-options-config.service';
import { IOption } from '@shared/models/icommons.model';
import { QueryCriteria } from '@records/records-search/shared/search-filters.model';
import {
  DATE_RANGE_FILTER_OPTIONS_LIST,
  DATE_RANGE_FILTER_REPORTS,
  DateRangeFilterOption,
  HIDE_SETTINGS_ICON_FOR_IDS,
  SHOW_SETTINGS_ICON_FOR_DROPDOWN_IDS,
  getDatesBySelectedOption,
} from './filter-option.const';
import { FilterTypes } from '../filter-set.model';
import type { CompoundFilterConfig, CompoundFilterGroupDef } from '@cbre/components';
import { COMPOUND_FILTER_CONFIGURATIONS } from '../../records-search.constants';
import {
  getCompoundFilterBuilder,
  injectCompoundFilterValues,
  resolveDefaultKeys,
} from '../../shared/compound-filter.factory';
import { RecordsSearchStateService } from '@records/shared/state/records-search-state.service';

// TODO (refactor): move this component to a shared place because is used in the FiltersBarComponent
// and in modules like Reports
@UntilDestroy()
@Component({
  selector: '[miq-filter-option]',
  templateUrl: './filter-option.component.html',
  styleUrls: ['./filter-option.component.scss'],
  standalone: false,
})
export class FilterOptionComponent implements OnInit, OnDestroy {
  @Input() form: UntypedFormGroup;
  @Input() filter: IOption;
  @Input() isReports = false;
  @Input() hideMenuToggles = false;
  includeEmpty: boolean;
  excludeEmpty: boolean;
  excludeSelection: boolean;
  dateFilterOptions: DateRangeFilterOption[] = [];
  filterOptionControl = new UntypedFormControl();
  disableDateFilter = false;
  private optionMenuIcons = {
    default: 'filter-option',
    selected: 'filter-option-green',
  };
  optionMenuIcon = this.optionMenuIcons.default;
  menuOptions: FilterOptionMenu[] = [];
  hideSettingsIcon = false;
  showSettingsIconForDropdown = false;

  // ── Compound filter state ────────────────────────────────────────────────
  // These are plain fields (not getters) computed once in initCompoundFilterState() and only
  // ever reassigned in response to explicit events (menu close). Keeping them referentially
  // stable across change-detection cycles matters: FilterOptionComponent isn't OnPush, so a
  // getter that allocates a new array/object on every read would feed a "new" reference into
  // CompoundFilterSelectionComponent's signal inputs on virtually every tick, re-triggering its
  // sync effect and fighting with the user's in-progress selection (visually: clicks that
  // "don't stick").
  /** Whether this filter has any compound-filter catalog entries at all. */
  hasCompoundFilters = false;
  /** Visual grouping for the "Search on…" checkbox tree — static config or auto-generated. */
  compoundFilterGroups: CompoundFilterGroupDef[] = [];
  /** Minimum-selection guard passed to the child component as `defaultSelectedKeys`. */
  compoundFilterDefaultKeys: number[] = [];
  /** Committed selection — only reassigned by onMenuClosed(), never read back on every render. */
  currentCompoundFilterKeys: number[] | undefined;
  /** Buffered selection while the menu is open; committed to search state on menu close. */
  private pendingCompoundFilterKeys: number[] | undefined;

  constructor(
    @Inject(FILTER_OPTION_SERVICE_TOKEN) private filterOptionService: FilterOptionService,
    @Inject(FILTER_OPTIONS_CONFIG_TOKEN) private filterOptions: FilterOptionConfig,
    private searchStateService: RecordsSearchStateService
  ) {}

  ngOnInit() {
    this.setFilterOptionControl();
    this.initDateFilterOptions();
    this.filterOptionService.clearNullFilter$.pipe(untilDestroyed(this)).subscribe(() => {
      this.filterOptionControl.setValue('default');
    });
    this.searchStateService.clearRequest$.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.hasCompoundFilters) this.resetCompoundFilterSelection();
    });
    this.disableFormOnExcludeEmpty();

    this.hideSettingsIcon = this.shouldHideSettingsIcon();
    this.showSettingsIconForDropdown = SHOW_SETTINGS_ICON_FOR_DROPDOWN_IDS.includes(this.filter.key);
    this.menuOptions = this.filterOptions.getFilterOptionsMenu({ isReports: this.isReports, filter: this.filter });

    this.initCompoundFilterState();
  }

  #isCurrentFilterAttributeOn = (attribute: string) => (sourceFilter: QueryCriteria) => {
    const filterIdentifier = this.filter?.key ?? this.filter?.field;
    const sourceIdentifier = sourceFilter?.key ?? sourceFilter?.field;
    return sourceIdentifier === filterIdentifier && sourceFilter[attribute];
  };
  #makeAttributeEvaluator = (attributeChecker: (x) => boolean) => (filter: QueryCriteria) =>
    filter.type === FilterTypes.Group ? filter.values.some(attributeChecker) : attributeChecker(filter);

  private setFilterOptionControl() {
    const { filters } = this.filterOptionService.searchParams;

    const isIncludeEmptyOn = this.#isCurrentFilterAttributeOn(INCLUDE_EMPTY.value);
    const isExcludeEmptyOn = this.#isCurrentFilterAttributeOn(EXCLUDE_EMPTY.value);
    const isExcludeSelection = this.#isCurrentFilterAttributeOn(EXCLUDE_SELECTION.value);

    this.includeEmpty = filters.some(this.#makeAttributeEvaluator(isIncludeEmptyOn));
    this.excludeEmpty = filters.some(this.#makeAttributeEvaluator(isExcludeEmptyOn));
    this.excludeSelection = filters.some(this.#makeAttributeEvaluator(isExcludeSelection));

    let selectedOption = DEFAULT_OPT.value;
    if (this.includeEmpty) {
      selectedOption = INCLUDE_EMPTY.value;
    } else if (this.excludeEmpty) {
      selectedOption = EXCLUDE_EMPTY.value;
    } else if (this.excludeSelection) {
      selectedOption = EXCLUDE_SELECTION.value;
    }
    this.filterOptionControl.setValue(selectedOption);
    this.filterOptionControl.valueChanges.subscribe(value => {
      this.includeEmpty = value === INCLUDE_EMPTY.value;
      this.excludeEmpty = value === EXCLUDE_EMPTY.value;
      this.excludeSelection = value === EXCLUDE_SELECTION.value;
      const newFilters = this.filterFormState();
      this.filterOptionService.searchQuery(newFilters);
      this.optionMenuIcon = this.getSvgIcon();
    });
    this.optionMenuIcon = this.getSvgIcon();
  }

  private shouldHideSettingsIcon(): boolean {
    const isNonRangeMonthFilter =
      this.filter?.dataType?.toLowerCase() === 'month' && this.filter?.type !== FilterTypes.Range;
    return HIDE_SETTINGS_ICON_FOR_IDS.includes(this.filter?.key) || isNonRangeMonthFilter;
  }

  #isFilterPartOfAGroup = (currentFilter, relatedFilter) =>
    currentFilter.group_key != null &&
    relatedFilter.key !== currentFilter.key &&
    relatedFilter.group_key === currentFilter.group_key;

  #hasTheFilterAssociations = (currentFilter, relatedFilter) =>
    Array.isArray(currentFilter.associations) &&
    currentFilter.associations.length > 0 &&
    relatedFilter.type === 'group';

  private filterFormState() {
    const { filters } = this.filterOptionService.searchParams;
    const newFilters = filters.map(eachFilter => {
      if (
        (this.#hasTheFilterAssociations(this.filter, eachFilter) ||
          this.#isFilterPartOfAGroup(this.filter, eachFilter)) &&
        Array.isArray(eachFilter.values)
      ) {
        const newValues = eachFilter.values.map(each => this.assignFilterOptions(each));
        return newValues.some((value, index) => value !== eachFilter.values[index])
          ? { ...eachFilter, values: newValues }
          : eachFilter;
      }
      return this.assignFilterOptions(eachFilter);
    });
    this.disableFormOnExcludeEmpty();
    return newFilters;
  }
  private assignFilterOptions(eachFilter: IOption): IOption {
    if ((eachFilter['key'] ?? eachFilter['field']) === (this.filter['key'] ?? this.filter['field'])) {
      const options =
        this.excludeEmpty && eachFilter.values
          ? {
              values: null,
              includeEmpty: this.includeEmpty,
              excludeEmpty: this.excludeEmpty,
              excludeSelection: this.excludeSelection,
            }
          : {
              includeEmpty: this.includeEmpty,
              excludeEmpty: this.excludeEmpty,
              excludeSelection: this.excludeSelection,
            };
      return { ...eachFilter, ...options };
    }
    return eachFilter;
  }
  private disableFormOnExcludeEmpty() {
    if (this.excludeEmpty) {
      this.disableDateFilter = true;
      const defaultValue = this.filter?.type === 'range' ? { from: null, to: null } : null;
      this.parentGroupControl?.reset(defaultValue, { emitEvent: false });
      this.parentGroupControl?.disable({ emitEvent: false });
      return;
    }
    this.disableDateFilter = false;
    this.parentGroupControl?.enable({ onlySelf: true, emitEvent: false });
  }

  get isDateRangeFilter() {
    return this.filter?.type === 'range' && (this.filter?.dataType === 'date' || this.filter?.inputType === 'date');
  }

  private initDateFilterOptions() {
    if (this.isDateRangeFilter) {
      this.dateFilterOptions = this.isReports ? DATE_RANGE_FILTER_REPORTS : DATE_RANGE_FILTER_OPTIONS_LIST;
    }
  }

  private setFromToValue(from: string, to: string) {
    this.fromControl.setValue(from);
    this.toControl.setValue(to);
  }

  setDateRangeFilter(dateOption: DateRangeFilterOption) {
    if (this.fromControl && this.toControl) {
      const { from, to } = getDatesBySelectedOption(dateOption.key, this.isReports);
      this.setFromToValue(from, to);
      // Used in the landing page to save the save the selected period by the user
      if (this.enumSelectionControl) {
        this.enumSelectionControl.setValue(dateOption.key);
      }
    }
  }

  get parentGroupControl(): UntypedFormGroup | UntypedFormControl {
    return (this.form.controls[this.filter?.key] || this.form.controls[this.filter?.field]) as
      | UntypedFormGroup
      | UntypedFormControl;
  }

  /* Used only for Date Range Filters  */
  get fromControl(): UntypedFormControl {
    return (this.parentGroupControl as UntypedFormGroup).controls?.['from'] as UntypedFormControl;
  }

  get toControl(): UntypedFormControl {
    return (this.parentGroupControl as UntypedFormGroup).controls?.['to'] as UntypedFormControl;
  }

  get enumSelectionControl(): UntypedFormControl {
    return (this.parentGroupControl as UntypedFormGroup).controls?.['enumSelection'] as UntypedFormControl;
  }

  /**
   * Get the svg icon to show in the filterOptionMenu
   * @returns
   */
  getSvgIcon(): string {
    const isPopulationNonDefault = [INCLUDE_EMPTY.value, EXCLUDE_EMPTY.value, EXCLUDE_SELECTION.value].includes(
      this.filterOptionControl.value
    );
    return isPopulationNonDefault || this.isCompoundSelectionNonDefault()
      ? this.optionMenuIcons.selected
      : this.optionMenuIcons.default;
  }

  // ── Compound filter helpers ──────────────────────────────────────────────

  /**
   * Computes all compound-filter related state ONCE, on init. Deliberately NOT exposed as
   * getters bound in the template: this component isn't OnPush, so a getter re-evaluated on
   * every change-detection tick would hand CompoundFilterSelectionComponent a brand-new
   * array/object reference almost every tick, re-triggering its input-sync effect and
   * clobbering the user's in-progress local selection (visually: clicks that don't stick).
   */
  private initCompoundFilterState(): void {
    this.hasCompoundFilters = this.catalogCompoundFilters.length > 0;
    if (!this.hasCompoundFilters) return;

    this.compoundFilterGroups = this.buildCompoundFilterGroups();
    this.compoundFilterDefaultKeys = resolveDefaultKeys(this.catalogCompoundFilters, this.compoundFilterConfig);
    this.currentCompoundFilterKeys = this.readCurrentCompoundFilterKeys();
    this.optionMenuIcon = this.getSvgIcon();
  }

  private resetCompoundFilterSelection(): void {
    if (!this.hasCompoundFilters) return;
    this.pendingCompoundFilterKeys = undefined;
    this.currentCompoundFilterKeys = [...this.compoundFilterDefaultKeys];
    this.optionMenuIcon = this.getSvgIcon();
  }

  /** Static customization for this filter's compound-filter UI, if one has been configured. */
  private get compoundFilterConfig(): CompoundFilterConfig | undefined {
    return COMPOUND_FILTER_CONFIGURATIONS[this.filter?.key];
  }

  /**
   * Visual grouping definitions for the compound filter selection UI.
   * Uses static config from COMPOUND_FILTER_CONFIGURATIONS when available.
   * Falls back to a flat auto-generated group (one item per compound filter)
   * built from the catalog IOptions on `filter.compoundFilters`, so that all
   * filters with server-provided compoundFilters work without any config entry.
   *
   * A static config only needs to describe the grouping it wants to CUSTOMIZE — it is NOT
   * required to enumerate every catalog compound filter. Any catalog entry the config's groups
   * don't mention ("orphans") is still appended as an auto-generated single-item group via
   * orphanCatalogFilters/autoGenerateGroups, same "config is a customization, not a
   * requirement" rule already applied when there's no config at all (see
   * resolveInitialSeedKeys/resolveDefaultKeys in compound-filter.factory.ts). Without this, an
   * orphan would be both invisible AND unselectable in the UI, even though
   * resolveInitialSeedKeys already includes it when seeding "select all" on first add (it reads
   * the raw catalog, not these groups) — i.e. the search could silently be broader than what
   * the checkboxes show.
   */
  private buildCompoundFilterGroups(): CompoundFilterGroupDef[] {
    const staticGroups = this.compoundFilterConfig?.groups;
    if (!staticGroups?.length) return this.autoGenerateGroups(this.catalogCompoundFilters);

    return [
      ...this.hydrateStaticGroups(staticGroups),
      ...this.autoGenerateGroups(this.orphanCatalogFilters(staticGroups)),
    ];
  }

  /**
   * Catalog compound filters (live BE catalog) not referenced by any item in the static
   * config's groups. These "orphans" must still surface in the UI, and remain selectable,
   * even though the config didn't explicitly place them in a group.
   */
  private orphanCatalogFilters(staticGroups: CompoundFilterGroupDef[]): IOption[] {
    const configuredKeys = new Set(staticGroups.flatMap(group => group.items.map(item => item.key)));
    return this.catalogCompoundFilters.filter(cf => !configuredKeys.has(cf.key));
  }

  /** Builds one standalone single-item group per catalog compound filter passed in. */
  private autoGenerateGroups(catalogFilters: IOption[]): CompoundFilterGroupDef[] {
    return catalogFilters.map(cf => ({
      label: cf.value,
      items: [{ key: cf.key, label: cf.value }],
    }));
  }

  /**
   * Static config groups are only expected to define `key` (grouping/ordering is the
   * customization) — the actual checkbox display label always comes from the live BE
   * catalog (`catalogCompoundFilters`) so it can never go stale relative to a
   * hardcoded FE string. An explicit `label` on a config item is still honored if present.
   *
   * Any group item whose key is no longer present in the live catalog is dropped (e.g. a
   * compound option retired on the BE, or simply not returned for this entity/environment),
   * and any group left with zero items after that is dropped entirely.
   *
   * A group left with exactly one item also gets its GROUP-level `label` backfilled (when not
   * explicitly configured) from that item's resolved label. `CompoundFilterSelectionComponent`'s
   * template renders single-item groups as a standalone checkbox using `group.label`, not
   * `item.label` — so a hardcoded single-item entry like `{ items: [{ key: 18768 }] }` (the
   * existing orphan pattern in e.g. COMPANY_PARTY_GROUPS) would otherwise render with a blank
   * checkbox.
   */
  private hydrateStaticGroups(staticGroups: CompoundFilterGroupDef[]): CompoundFilterGroupDef[] {
    const catalogByKey = new Map(this.catalogCompoundFilters.map(cf => [cf.key, cf]));
    return staticGroups
      .map(group => {
        const items = group.items
          .filter(item => catalogByKey.has(item.key))
          .map(item => ({ ...item, label: item.label ?? catalogByKey.get(item.key)?.value }));
        const label = group.label ?? (items.length === 1 ? items[0].label : undefined);
        return { ...group, items, label };
      })
      .filter(group => group.items.length > 0);
  }

  /**
   * Keys currently selected for this filter's compound filters, read ONCE from the live
   * search params (via the type-specific CompoundFilterBuilder) so the UI reflects stored
   * state. Entries no longer present in the catalog are excluded. Returns `undefined` when
   * no prior selection exists — the child component will fall back to `defaultSelectedKeys`.
   */
  private readCurrentCompoundFilterKeys(): number[] | undefined {
    const current = this.findParentFilterInParams();
    if (!current) return undefined;

    const builder = getCompoundFilterBuilder(this.filter);
    const storedKeys = builder.readSelectedKeys(current, this.filter?.key);
    if (!storedKeys?.length) return undefined;

    const availableKeys = new Set(this.catalogCompoundFilters.map(cf => cf.key));
    const safeKeys = storedKeys.filter(key => availableKeys.has(key));
    return safeKeys.length ? safeKeys : undefined;
  }

  /**
   * Finds the parent QueryCriteria for this filter in the current search params.
   * Works for both standalone filters (direct match by key) and group filters
   * (group root also shares the same key as the IOption).
   */
  private findParentFilterInParams(): QueryCriteria | undefined {
    const { filters } = this.filterOptionService.searchParams as { filters: QueryCriteria[] };
    return filters?.find((f: QueryCriteria) => f.key === this.filter?.key);
  }

  /**
   * The catalog compound filters for this parent filter, taken as-is from the live BE catalog
   * (`filter.compoundFilters`).
   *
   * This used to exclude any entry whose `key` matched the parent filter's own key, on the
   * theory that it would mean "the same field searched twice" (once as the main criterion,
   * once as a compound filter) — see git history / MIQ bug report for the Contact "Company ID"
   * filter (catalog key 11520) if this comment is ever removed. That theory doesn't hold for
   * real BE data: BE legitimately reuses the parent's own key for a compound-filter entry
   * representing "search on the base field itself" (sometimes the ONLY entry for a filter, as
   * with Contact Companies_CompanyID). Excluding it made that filter's entire compound-filter UI
   * invisible (`hasCompoundFilters` false) even though its own config declared that exact key as
   * `defaultSelectedKeys`. Trust the catalog as given, same as everywhere else in this file.
   */
  private get catalogCompoundFilters(): IOption[] {
    return (this.filter?.compoundFilters as IOption[]) ?? [];
  }

  /**
   * Compares the currently checked keys (pending, else committed, else default) against
   * `compoundFilterDefaultKeys` to decide whether the gear icon should render as "selected".
   */
  private isCompoundSelectionNonDefault(): boolean {
    if (!this.hasCompoundFilters) return false;
    const current = this.pendingCompoundFilterKeys ?? this.currentCompoundFilterKeys ?? this.compoundFilterDefaultKeys;
    if (current.length !== this.compoundFilterDefaultKeys.length) return true;
    const defaultKeys = new Set(this.compoundFilterDefaultKeys);
    return !current.every(key => defaultKeys.has(key));
  }

  /**
   * Buffers the user's in-progress selection from CompoundFilterSelectionComponent.
   * Deliberately does NOT dispatch a search on every toggle — only updates the icon preview.
   * The selection is only committed to search state when the menu closes (onMenuClosed),
   * so a fast series of checkbox clicks results in exactly one search request, and the
   * live-reflected `[selectedKeys]` input never round-trips through global search state
   * while the user is still interacting with it.
   */
  onCompoundFilterSelectionChange(selectedKeys: number[]): void {
    this.pendingCompoundFilterKeys = selectedKeys;
    this.optionMenuIcon = this.getSvgIcon();
  }

  /**
   * Commits the buffered compound filter selection (if any) to search state and dispatches
   * a single search request. Called when the settings mat-menu closes, regardless of how it
   * was closed (checkbox click outside the panel bounds, escape key, etc.).
   */
  onMenuClosed(): void {
    if (!this.hasCompoundFilters || this.pendingCompoundFilterKeys === undefined) return;

    const selectedKeys = this.pendingCompoundFilterKeys;
    this.pendingCompoundFilterKeys = undefined;

    const { filters } = this.filterOptionService.searchParams as { filters: QueryCriteria[] };
    const current = this.findParentFilterInParams();
    if (!current || !filters) return;

    const builder = getCompoundFilterBuilder(this.filter);
    const updated = builder.applySelection(current, this.filter?.key, selectedKeys, this.catalogCompoundFilters);
    const updatedWithValues = injectCompoundFilterValues(updated);

    const newFilters = filters.map((f: QueryCriteria) => (f.key === this.filter?.key ? updatedWithValues : f));

    this.currentCompoundFilterKeys = selectedKeys;
    this.optionMenuIcon = this.getSvgIcon();
    this.filterOptionService.searchQuery(newFilters);
  }

  ngOnDestroy(): void {
    // Required by untilDestroyed
  }
}
